import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, DOCUMENT } from '@angular/core';
import { Router } from '@angular/router';
import { paths } from '@fleet/environment';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { TimeOverride } from './product-configuration.model';

import { InjectionToken } from '@angular/core';
import { merge } from 'lodash-es';


import { Title } from '@angular/platform-browser';

import * as WebFont from 'webfontloader';
import {
  CssStyleDeclaration,
  generateMaterialSystemVariables,
  generateStylesByColorHex,
} from './brand-utilities/css-theme-generator';

import { LocaleService } from '@fleet/locale/locale-service/locale.service';

import { AreaModel, BrandSettingModel } from '@fleet/model';

import { fleetBrand } from './brand_assets/fleet-brand';

import { retryOn409 } from '@fleet/utilities/retry-on-409';
import { TranslocoService } from '@jsverse/transloco';
export const PRODUCT_CONFIG = new InjectionToken<any>('PRODUCT_CONFIG');

/** sessionStorage key: URL to return to from maintenance when brand/config fails */
export const MAINTENANCE_RETURN_URL_KEY = 'maintenanceReturnUrl';

@Injectable({
  providedIn: 'root',
})
export class ProductConfigurationService {
  /**
   * Dev fallback when brand config has no `timeOverride`.
   * Set to `'24'` or `null` to test other modes; remove when API ships.
   */
  private static readonly TIME_OVERRIDE_DEV_DEFAULT: TimeOverride | null = '12';

  productConfiguration: BehaviorSubject<any> = new BehaviorSubject(null);
  nonAuthorisedHttp: HttpClient;
  private _config: BehaviorSubject<any>;
  host: string;
  headers: any;
  fleetProduct: string;
  originalBrand: BrandSettingModel;
  brand: BrandSettingModel;
  serviceArea: BehaviorSubject<AreaModel> = new BehaviorSubject(null);
  networkConfig: BehaviorSubject<any> = new BehaviorSubject(null);
  constructor(
    private http: HttpClient,
    private backend: HttpBackend,
    @Inject('env') env: any,
    @Inject(PRODUCT_CONFIG) config: any,
    @Inject(DOCUMENT) private readonly document: Document,
    private titleService: Title,
    private localeService: LocaleService,
    private translocoService: TranslocoService,
    private router: Router
  ) {
    this.nonAuthorisedHttp = new HttpClient(backend);
    this.host = env.host + paths.productConfiguration;
    this.fleetProduct = env.fleetProduct;
    this._config = new BehaviorSubject(config);
    this.headers = new HttpHeaders({
      'fleet-channel': 'WEB',
      'fleet-product': env.fleetProduct,
    });
  }

  get networkConfig$() {
    return this.networkConfig.asObservable();
  }

  get networkConfigValue() {
    return this.networkConfig.value;
  }

  get serviceArea$() {
    return this.serviceArea.asObservable();
  }

  getProductConfiguration(): Promise<any> {
    return this.nonAuthorisedHttp
      .get(this.host, { headers: this.headers })
      .pipe(
        retryOn409(),
        tap((resp: any) => {
          this.networkConfig.next({
            networkId: resp.data.networkId,
            networkGroupId: resp.data.networkGroupId,
          });

          // Check for brand override query parameter
          const urlParams = new URLSearchParams(window.location.search);
          const brandOverride = urlParams.get('brandOverride');

          if (brandOverride === 'fleet') {
            // Merge fleet brand over server data to keep missing properties like locale, bounds, etc.
            this.originalBrand = merge({}, resp.data, fleetBrand);
            this.loadBrandConfig(this.originalBrand);
          } else {
            const hasLocalConfig = localStorage.getItem('brandConfig');
            if (hasLocalConfig) {
              this.originalBrand = JSON.parse(hasLocalConfig);
              this.loadBrandConfig(this.originalBrand);
            } else {
              const brand = resp.data;

              this.loadBrandConfig(brand);
            }
          }
        }),
        catchError((error) => {
          console.error(
            'Product configuration request failed',
            error?.status ?? 'unknown',
            error
          );
          this.applySafeFallbackBrandAndNavigateToMaintenance(error);
          return of(true);
        })
      )
      .toPromise();
  }

  /**
   * Loads bundled brand and navigates to /maintenance so the user sees recovery UI.
   * Deferred so APP_INITIALIZER can resolve before the router runs initial navigation.
   */
  private applySafeFallbackBrandAndNavigateToMaintenance(error: {
    status?: number;
  }): void {
    this.originalBrand = merge({}, fleetBrand);
    this.loadBrandConfig(this.originalBrand);
    try {
      sessionStorage.setItem(MAINTENANCE_RETURN_URL_KEY, window.location.href);
    } catch {
      /* storage may be unavailable */
    }
    const status = error?.status;
    const reason =
      status === 503 || status === 502 || status === 0
        ? 'unavailable'
        : 'brand';
    setTimeout(() => {
      void this.router.navigate(['/maintenance'], {
        queryParams: { reason },
      });
    }, 0);
  }

  loadBrandConfig(config: BrandSettingModel | any) {
    if (config == null) {
      this.loadBrandConfig(merge({}, fleetBrand));
      return;
    }

    const favIcon = document.querySelector(
      '#appIcon'
    ) as HTMLLinkElement | null;
    const productMatch = config.productNames?.find(
      (s: any) => s.fleetProduct === this.fleetProduct
    );
    const pageTitle = productMatch?.name ?? config.brandName ?? 'Fleet';

    this.titleService.setTitle(pageTitle);
    if (favIcon && config.favIcon) {
      favIcon.href = config.favIcon;
    }

    // Ensure bounds has a point property with center lat/lng
    if (config.bounds && !config.bounds.point) {
      // Calculate center point from northWest and southEast if available
      if (config.bounds.northWest && config.bounds.southEast) {
        const centerLat =
          (parseFloat(config.bounds.northWest.latitude) +
            parseFloat(config.bounds.southEast.latitude)) /
          2;
        const centerLng =
          (parseFloat(config.bounds.northWest.longitude) +
            parseFloat(config.bounds.southEast.longitude)) /
          2;

        config.bounds.point = {
          latitude: centerLat.toString(),
          longitude: centerLng.toString(),
        };
      }
    }

    this.serviceArea.next(config.bounds);
    //Turn this off
    // config.locale = 'es';

    if (config.locale && config.locale !== this.localeService.getLocale()) {
      this.localeService.setLocale(config.locale);
      // this.translocoService.setActiveLang(
      //   config.locale.replace(/-/g, '_').toLowerCase()
      // );
      // this.localeService.setLocale('en-US');
    }
    if (config.currencyCode) {
      this.localeService.setCurrency(config.currencyCode);
      // this.localeService.setCurrency('USD');
    }

    const theme = config.theme ?? fleetBrand.theme;
    this.setCssVariables(theme, this.document);
    if (config.font) {
      this.loadFont(config.font);
    }

    const localeTag =
      typeof config.locale === 'string' && config.locale.length > 0
        ? config.locale.replace(/-/g, '_').toLowerCase()
        : 'en_au';
    this.translocoService.load(localeTag).subscribe({});

    //check if layout is flex
    const flexParam = window.location.search.includes('layout=flex');
    if (flexParam) {
      config.layout = 'flex';
    }
    this.config = config;
    this.brand = config;
  }

  /**
   * Constructor
   */

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for config
   */
  set config(value: any) {
    // Merge the new config over to the current config
    const config = merge({}, this._config.getValue(), value);

    // Execute the observable
    this._config.next(config);
  }

  get config$(): Observable<any> {
    return this._config.asObservable();
  }

  /**
   * Brand `timeOverride` when set; otherwise {@link TIME_OVERRIDE_DEV_DEFAULT} for local testing.
   */
  get timeOverride$(): Observable<TimeOverride | null> {
    return this.config$.pipe(
      map((config) => this.resolveTimeOverride(config)),
      distinctUntilChanged()
    );
  }

  get timeOverrideValue(): TimeOverride | null {
    return this.resolveTimeOverride(this.configValue);
  }

  private resolveTimeOverride(config: unknown): TimeOverride | null {
    const override = (config as { timeOverride?: string | null })?.timeOverride;
    if (override === '12' || override === '24') {
      return override;
    }
    return ProductConfigurationService.TIME_OVERRIDE_DEV_DEFAULT;
  }

  get configValue() {
    return this._config.value;
  }

  getLayout() {
    return this._config.value.layout as string;
  }

  publish() {
    localStorage.setItem('brandConfig', JSON.stringify(this._config.value));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Resets the config to the default
   */
  reset(): void {
    // Set the config
    this._config.next(this.config);
  }

  setCssVariables(theme: any, document: Document) {
    if (!theme?.primary) {
      return;
    }
    let styles: CssStyleDeclaration[] = [];
    styles = [...styles, ...generateStylesByColorHex(theme.primary, 'primary')];

    if (theme.accent) {
      styles = [...styles, ...generateStylesByColorHex(theme.accent, 'accent')];
    }

    //warn
    styles = [
      ...styles,
      ...generateStylesByColorHex(theme.warn ? theme.warn : '#dc2626', 'warn'),
    ];

    //control colors
    styles = [
      ...styles,
      ...generateStylesByColorHex(
        theme.controls ? theme.controls : theme.primary,
        'controls'
      ),
    ];
    styles = [
      ...styles,
      ...generateMaterialSystemVariables(theme),
    ];

    styles.forEach((style) => {
      document.body.style.setProperty(style.var, style.value);
    });
  }

  loadFont(font: string) {
    WebFont.load({
      google: {
        families: [font],
      },
    });
    document.documentElement.style.setProperty('--app-font', font);
  }
}
